/**
 * Pole daty numeracji typu zadania (`task_types.numbering_base_field`, kartoteka Zadania).
 *
 * Typ zadania może wskazać pole daty, z którego liczą się rok i miesiąc w numerze oraz klucz
 * resetu licznika — tak jak „pole daty numeracji" typu dokumentu. Zbiór dozwolony = pola
 * systemowe zadania (stała niżej) ∪ pola dodatkowe kartoteki `tasks` typu data/timestamp
 * (sluginy dociąga serwer z katalogu pól dodatkowych tenanta).
 *
 * Brak wyboru (`null`) = data utworzenia zadania (`DEFAULT_TASK_NUMBERING_BASE_SLUG`); serwer
 * zapisuje ten wybór zawsze jako `null` (także przysłany jawnie slug daty utworzenia). Dzień do
 * numeracji serwer liczy w UTC dla każdego pola (jak w dokumentach).
 */

import type { NumberingBaseCoreDateField } from './field-types';

/**
 * Pole systemowe zadania, które może być podstawą numeracji (wspólny kształt z etykietą).
 * Slug = nazwa kolumny `tasks`; `payloadKey` = klucz w ciele żądania POST/PATCH `/api/tasks` —
 * `created_at` nie przychodzi w żądaniu, jego `payloadKey` służy wyłącznie etykiecie.
 */
export type TaskCoreDateField = Required<NumberingBaseCoreDateField>;

/** Przestrzeń kluczy i18n etykiet pól daty numeracji w edytorze typu zadania. */
export const TASK_NUMBERING_BASE_I18N = 'tenant.tasks.types.numberingBase';

/** Definicja pola systemowego z dokładnymi typami sluga, klucza żądania i klucza etykiety. */
interface TaskCoreDateFieldOf<S extends string, K extends string> extends TaskCoreDateField {
  readonly slug: S;
  readonly payloadKey: K;
  readonly labelKey: `${typeof TASK_NUMBERING_BASE_I18N}.${K}`;
}

function coreDateField<const S extends string, const K extends string>(
  slug: S,
  payloadKey: K,
): TaskCoreDateFieldOf<S, K> {
  return { slug, payloadKey, labelKey: `${TASK_NUMBERING_BASE_I18N}.${payloadKey}` };
}

/** Pola systemowe zadania do wyboru jako podstawa numeracji — kolejność opcji w edytorze typu. */
export const TASK_CORE_DATE_FIELDS = [
  coreDateField('created_at', 'createdAt'),
  coreDateField('due_at', 'dueAt'),
  coreDateField('planned_start_at', 'plannedStartAt'),
  coreDateField('planned_end_at', 'plannedEndAt'),
  coreDateField('actual_start_at', 'actualStartAt'),
  coreDateField('actual_end_at', 'actualEndAt'),
] as const satisfies readonly TaskCoreDateField[];

/** Definicja jednego pola systemowego z `TASK_CORE_DATE_FIELDS` (typy dokładne). */
export type TaskCoreDateFieldDef = (typeof TASK_CORE_DATE_FIELDS)[number];

/** Slug pola systemowego zadania, które może być podstawą numeracji. */
export type TaskCoreDateSlug = TaskCoreDateFieldDef['slug'];

/** Klucz żądania pola systemowego zadania, które może być podstawą numeracji. */
export type TaskCoreDatePayloadKey = TaskCoreDateFieldDef['payloadKey'];

/** Podstawa numeracji typu bez wyboru (`numbering_base_field` = `null`): data utworzenia zadania. */
export const DEFAULT_TASK_NUMBERING_BASE_SLUG = 'created_at' satisfies TaskCoreDateSlug;

/** Definicja pola systemowego po slugu; `undefined` — slug nie jest polem systemowym zadania. */
export function findTaskCoreDateField(slug: string): TaskCoreDateFieldDef | undefined {
  return TASK_CORE_DATE_FIELDS.find((field) => field.slug === slug);
}

/** Czy `slug` jest polem systemowym zadania (porównanie dokładne). */
export function isTaskCoreDateSlug(slug: string): slug is TaskCoreDateSlug {
  return findTaskCoreDateField(slug) !== undefined;
}

/**
 * Czy `slug` jest dozwolonym polem daty numeracji typu zadania — pole systemowe
 * (`TASK_CORE_DATE_FIELDS`) albo pole dodatkowe kartoteki `tasks` typu data/timestamp,
 * przekazane w `extDateSlugs`. Porównanie dokładne (bez normalizacji wielkości liter).
 */
export function isKnownTaskNumberingBaseSlug(
  slug: string,
  extDateSlugs: readonly string[],
): boolean {
  if (isTaskCoreDateSlug(slug)) return true;
  return extDateSlugs.includes(slug);
}
